import { authTokens, setAuthTokens } from "@/data/store"
import { AuthTokens } from "@/model/AuthTokens"
import { Jwt } from "@/model/Jwt"
import { LoginResponse } from "@/model/LoginResponse"
import { Instant } from "@js-joda/core"
import { merge } from "lodash"

export const backendUrl = `${import.meta.env.VITE_BACKEND_API_URL as string}`
const appVersion = `${import.meta.env.VITE_APP_VERSION ?? "dev"}`

export class HttpError extends Error {
  statusCode: number
  responseJson?: Record<string, unknown>

  constructor(statusCode: number, url: string, responseJson?: Record<string, unknown>) {
    super(`HttpError: ${statusCode} ${url} ${JSON.stringify(responseJson)}`)
    this.statusCode = statusCode
    this.responseJson = responseJson
  }
}

export const jsonFetch = async <T>(url: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    headers: {
      "Content-Type": "application/json",
      "X-App-Version": appVersion,
    },
  }

  const response = await fetch(url, merge(defaultOptions, options))
  const responseBody = await response.text()
  const responseJson = responseBody ? JSON.parse(responseBody) : undefined

  if (!response.ok) {
    throw new HttpError(response.status, url, responseJson)
  }

  return responseJson as T
}

let refreshingTokensPromise: Promise<AuthTokens> | null = null

export const refreshTokens = (refreshTokenString: string): Promise<AuthTokens> => {
  if (refreshingTokensPromise) return refreshingTokensPromise

  refreshingTokensPromise = (async () => {
    try {
      const response = await jsonFetch<LoginResponse>(`${backendUrl}/v1/token/refresh`, {
        method: "POST",
        body: JSON.stringify({ refreshToken: refreshTokenString }),
      })
      const newTokens: AuthTokens = {
        accessJwt: response.accessJwt,
        refreshJwt: response.refreshJwt,
      }

      // Guard: only persist if our refresh token still matches what's in the store.
      // If the user logged out (store cleared) or a parallel session took over (different refresh),
      // the stale refresh result must NOT restore the old session.
      const current = authTokens()
      if (current?.refreshJwt.token === refreshTokenString) {
        setAuthTokens(newTokens)
        return newTokens
      }
      // Stale: surface as a refresh failure so the caller treats it as auth lost.
      throw new HttpError(401, `${backendUrl}/v1/token/refresh`, { reason: "stale-refresh" })
    } catch (error) {
      // BioT returns 403 for LOGIN_FAILED (token invalidated server-side) and 401 for expired refresh tokens.
      // Clear stored tokens on either so PostLoginRedirect sees null and renders sign-in normally.
      if (error instanceof HttpError && (error.statusCode === 401 || error.statusCode === 403)) {
        setAuthTokens(null)
      }
      throw error
    }
  })().finally(() => {
    refreshingTokensPromise = null
  })

  return refreshingTokensPromise
}

export const authenticatedFetch = async <T>(url: string, options: RequestInit = {}) => {
  const { accessJwt, refreshJwt } = authTokens()!
  const accessJwtExpiration = Instant.parse(accessJwt.expiration)
  const isAboutToExpire = Instant.now().isAfter(accessJwtExpiration.minusSeconds(150)) // User access tokens should stay valid for 15 minutes, we refresh them 2m30s in advance to be sure that it doesn't expire during a long running request.
  const refreshJwtString = refreshJwt.token
  let accessToken = accessJwt
  if (isAboutToExpire) {
    const refreshedTokens: AuthTokens = await refreshTokens(refreshJwtString)
    accessToken = refreshedTokens.accessJwt
  }
  try {
    return await _authenticatedFetch<T>({
      url,
      accessJwt: accessToken,
      options,
    })
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 401) {
      const refreshedTokens: AuthTokens = await refreshTokens(refreshJwtString)
      return await _authenticatedFetch<T>({
        url,
        accessJwt: refreshedTokens.accessJwt,
        options,
      })
    }
    throw error
  }
}

const _authenticatedFetch = async <T>({
  url,
  accessJwt,
  options = {},
}: {
  url: string
  accessJwt: Jwt
  options?: RequestInit
}) => {
  const defaultOptions = {
    headers: {
      Authorization: `Bearer ${accessJwt.token}`,
    },
  }
  return await jsonFetch<T>(url, merge(defaultOptions, options))
}

export const logOut = () => {
  jsonFetch(`${backendUrl}/v1/logout`, {
    method: "POST",
    body: JSON.stringify({
      refreshToken: authTokens()!.refreshJwt.token,
    }),
  }).catch(() => {}) // Run the logout request to the backend, but don't wait for it or handle the error
  setAuthTokens(null)
}
